function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useState } from 'react';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { Directions } from 'common/components/drop-menu/types';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { ChangeEvent } from 'common/components/select/types';
import { DisplaySize, Placement } from 'common/components/tooltip/types';
import { CountBy, MetricEntry, RetentionCountType } from 'common/query-builder/query-entry-section/types';
import { CountUsersOnceState } from 'common/report/constants';
import { ChartType, MetricType, MultiAttributionType, RETENTION_MATH_TYPES } from 'common/types/reports/bookmark';
// eslint-disable-next-line import/no-cycle
import { getRetentionMetricAttributionItems } from 'common/report/insights/util/retention-attribution';
import { MultiAttributionMenu } from 'common/widgets/multi-attribution-menu';
import { DefaultMultiAttribution } from 'common/widgets/multi-attribution-menu/constants';
import { getCountUsersOnceTypeFromEntrySelection } from 'common/query-builder/query-block/count-menu/util';
import { useBehaviors } from 'common/contexts/react/behaviors-context';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useTranslation } from 'common/i18n/hooks';
import { getMultiAttributionFromEntry, isSupportedMultiAttributionTypeForEntry } from 'common/query-builder/query-builder/util';
import { getMultiAttributionTitle, getUpdatedMultiAttribution } from 'common/query-builder/query-entry-section/util/multi-attribution';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { QueryEntrySectionTrackingEvent } from 'common/query-builder/query-entry-section/tracking';
import { CountQueryBlock } from 'common/query-builder/query-block/count-menu/count-query-block';
import { useDataGroups } from 'common/contexts/react/data-group-context';
import { getFunnelOrRetentionDataGroupId } from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/behavior-utils';
import type { RowProps } from '../catalog';
export function RetentionMetricMeasurement(props: RowProps) {
  var _getRetentionMetricAt, _props$entry$behavior;
  const metrics = useMetrics();
  const behaviors = useBehaviors();
  const {
    t,
    print
  } = useTranslation();
  const currentProject = useCurrentProject().get().value;
  const dataGroupStore = useDataGroups();
  const [isMultiAttributionOpen, setIsMultiAttributionOpen] = useState(false);
  const dataGroupId = getFunnelOrRetentionDataGroupId({
    currentProject,
    props
  });
  const handleRetentionCountBySelected = (countBy: CountBy) => {
    const measurement = props.entry.measurement;
    measurement.aggregationOperator = countBy.aggregationOperator;
    measurement.countUsersOnceType = getCountUsersOnceTypeFromEntrySelection({
      selectedCountUsersOnceType: countBy.countUsersOnceType,
      metricType: MetricType.Retention,
      multiAttribution: measurement.multiAttribution
    });
    measurement.property = countBy.property;
    const entry = _objectSpread(_objectSpread({}, props.entry), {}, {
      measurement
    });
    props.updateEntry(entry);
    props.dispatchTrack(QueryEntrySectionTrackingEvent.SelectNumericAggregator, _objectSpread({
      /** @todo(metrics) tracking */
      // ...props.getEntryTrackingProps(),
      Aggregator: countBy.aggregationOperator
    }, countBy.property && {
      'Property name': countBy.property.label,
      'Property data type': "type" in countBy.property ? countBy.property.type : null
    }));
  };
  const handleRetentionBucketIndexSelected = (ev: ChangeEvent<number>) => {
    const retentionBucketIndex = ev.detail.item.value;
    const entry = _objectSpread(_objectSpread({}, props.entry), {}, {
      measurement: _objectSpread(_objectSpread({}, props.entry.measurement), {}, {
        retentionBucketIndex
      })
    });
    props.updateEntry(entry);
  };
  const handleAttributionSelection = (_ref: CustomEvent) => {
    let {
      detail
    }: CustomEvent = _ref;
    const entry = _objectSpread(_objectSpread({}, props.entry), {}, {
      measurement: _objectSpread(_objectSpread({}, props.entry.measurement), {}, {
        attributionSelection: detail.item.value
      })
    });
    props.updateEntry(entry);
  };
  const breakdownAttributionLabel = props.sectionProps.breakdownAttributionLabeler ? print(props.sectionProps.breakdownAttributionLabeler(print((_getRetentionMetricAt = getRetentionMetricAttributionItems(props.entry).find(entry => entry.isSelected)) === null || _getRetentionMetricAt === void 0 ? void 0 : _getRetentionMetricAt.label) || "...")) : "...";
  const multiAttribution = getMultiAttributionFromEntry(props.entry);
  const COUNT_TYPE_MAPPING = {
    [RETENTION_MATH_TYPES.RETENTION_RATE]: RetentionCountType.RetentionRate,
    [RETENTION_MATH_TYPES.UNIQUE]: RetentionCountType.Unique,
    [RETENTION_MATH_TYPES.PROPERTY_AVERAGE]: RetentionCountType.PropertyAverage,
    [RETENTION_MATH_TYPES.PROPERTY_SUM]: RetentionCountType.PropertySum
  };
  return <>
      {/* Measurement selection like Retention Rate, Property Aggregation, etc... */}
      {/* @todo(metrics) styles are messed up for measurement blocks */}
      <CountQueryBlock className='count-menu' elref='countBlock' theme={ButtonTheme.Tertiary} buttonSize={ButtonSize.Inline} metricType={props.entry.behavior.type} dataGroupId={dataGroupId} dataGroups={Object.values(dataGroupStore.value)} projectId={props.sectionProps.projectId} countUsersOnceState={props.sectionProps.canUseMultiAttribution ? CountUsersOnceState.Required : props.sectionProps.countUsersOnceState} selected={{
      countType: COUNT_TYPE_MAPPING[props.entry.measurement.aggregationOperator],
      aggregationOperator: props.entry.measurement.aggregationOperator,
      property: props.entry.measurement.property,
      countUsersOnceType: props.entry.measurement.countUsersOnceType
    }} eventNames={[(_props$entry$behavior = props.entry.behavior.behaviors[1]) === null || _props$entry$behavior === void 0 ? void 0 : _props$entry$behavior.name]} trackName='retention' userId={props.sectionProps.userId} workspaceId={props.sectionProps.workspaceId} onSelect={handleRetentionCountBySelected} includeCustomProperties={props.sectionProps.includeCustomProperties} showBorrowedProperties={props.sectionProps.showBorrowedProperties} data-sentry-element="CountQueryBlock" data-sentry-source-file="measurement.tsx" />

      {/* Retention bucket selector */}
      {props.sectionProps.chartType === ChartType.RetentionCurve ? <SelectReact alwaysShowCaret theme={SelectTheme.Tertiary} size={ButtonSize.Small} menuAlignment={Directions.DownRight} menuWidth={184} disableFirstItemPadding elref='retentionBucketIndexSelect' sections={[{
      label: t("query-builder.retention.groups.title"),
      icon: "info-xs",
      tooltip: {
        'max-width': "120px",
        placement: Placement.Right,
        text: t("query-builder.retention.groups.tooltip")
      },
      items: [{
        label: t("query-builder.retention.groups.all"),
        isSelected: true,
        value: null
      }]
    }]} /> : <SelectReact alwaysShowCaret theme={SelectTheme.Tertiary} size={ButtonSize.Small} menuAlignment={Directions.DownRight} menuWidth={184} disableFirstItemPadding elref='retentionBucketIndexSelect' sections={[{
      label: t("query-builder.retention.groups.title"),
      icon: "info-xs",
      tooltip: {
        'max-width': "120px",
        placement: Placement.Right,
        text: t("query-builder.retention.groups.tooltip")
      },
      items: props.entry.measurement.retentionBuckets
    }]} onChange={handleRetentionBucketIndexSelected} />}

      {/* Event attribution selection (selects breakdowns on Step 1 or Step 2) */}
      {!props.sectionProps.canUseMultiAttribution && props.sectionProps.canUseBreakdownAttribution && <div className='mp-metric-entry-measurement-inner'>
          <SelectReact elref='attributionSelect' alwaysShowCaret sections={[{
        items: getRetentionMetricAttributionItems(props.entry)
      }]} label={breakdownAttributionLabel} selectedOption='' size={ButtonSize.Small} theme={SelectTheme.Tertiary} menuWidth={0} tabIndex={0} experimentalPositioning onChange={handleAttributionSelection} />
        </div>}

      {/* Attribution model selection */}
      {props.sectionProps.canUseMultiAttribution && multiAttribution && <div className='mp-metric-entry-measurement-inner'>
          <SelectReact className='subitem-select.multi-attribution-type' elref='multiAttributionSelector' alwaysShowCaret theme={SelectTheme.Tertiary} size={ButtonSize.Small} tooltip={{
        text: "".concat(print(getMultiAttributionTitle(props.entry)), " ").concat(breakdownAttributionLabel, "."),
        size: DisplaySize.Medium
      }}>
            <div slot='body'>
              {isMultiAttributionOpen && <MultiAttributionMenu onCancel={() => setIsMultiAttributionOpen(false)} onChange={ma => props.updateEntry(getUpdatedMultiAttribution(props.entry, ma) as MetricEntry)} onDelete={() => props.updateEntry(getUpdatedMultiAttribution(props.entry, null) as MetricEntry)} isSupportedMultiAttributionType={type => isSupportedMultiAttributionTypeForEntry({
            entry: props.entry,
            multiAttributionType: type,
            metricsResource: metrics,
            behaviorsResource: behaviors
          })} showMultiAttributionType={type => [MultiAttributionType.LastTouch, MultiAttributionType.FirstTouch, MultiAttributionType.Participation].includes(type)} metricIndex={props.index} multiAttribution={multiAttribution || DefaultMultiAttribution} projectIsFreePlan={props.sectionProps.projectIsFreePlan} />}
            </div>
          </SelectReact>
        </div>}
    </>;
}